/**
 * Account Management Service (Admin)
 *
 * Provides methods for system administrators to manage accounts
 */

import api from '../api';
import type {
  AdminAccount,
  PaginatedResponse,
  AccountListParams,
  UpdateAccountData,
  AccountResponse,
  MessageResponse,
} from '../../types/admin';

/**
 * Get paginated list of accounts with filters
 */
export async function getAllAccounts(
  params?: AccountListParams
): Promise<PaginatedResponse<AdminAccount>> {
  const response = await api.get<PaginatedResponse<AdminAccount>>('/api/admin/accounts', {
    params,
  });
  return response.data;
}

/**
 * Get single account by ID with relationships
 */
export async function getAccountById(id: number): Promise<AdminAccount> {
  const response = await api.get<{ account: AdminAccount }>(`/api/admin/accounts/${id}`);
  return response.data.account;
}

/**
 * Update account details (admin)
 */
export async function updateAccount(
  id: number,
  data: UpdateAccountData
): Promise<AdminAccount> {
  const response = await api.put<AccountResponse>(`/api/admin/accounts/${id}`, data);
  return response.data.account;
}

/**
 * Delete account permanently
 */
export async function deleteAccount(id: number): Promise<MessageResponse> {
  const response = await api.delete<MessageResponse>(`/api/admin/accounts/${id}`);
  return response.data;
}

/**
 * Suspend account
 */
export async function suspendAccount(id: number): Promise<AdminAccount> {
  const response = await api.post<AccountResponse>(`/api/admin/accounts/${id}/suspend`);
  return response.data.account;
}

/**
 * Reactivate suspended account
 */
export async function reactivateAccount(id: number): Promise<AdminAccount> {
  const response = await api.post<AccountResponse>(`/api/admin/accounts/${id}/reactivate`);
  return response.data.account;
}

/**
 * Account management service object
 */
export const accountManagementService = {
  getAllAccounts,
  getAccountById,
  updateAccount,
  deleteAccount,
  suspendAccount,
  reactivateAccount,
};
